import { useMemo } from 'react';

import dayjs from 'dayjs';

import { DATE_SIMPLE } from '@cast/constants';
import {
  Column,
  ColumnGroup,
  DataTable,
  TableHeader,
  Typography,
} from '@cast/design-system';
import { dashesOrNum } from '@cast/utils';

import { ReliabilityMetricsData } from 'features/cluster/_components/reliability/_utils/buildReliabilityChartData';
import { formatLatencyWithUnit } from 'features/cluster/_components/reliability/_utils/formatLatency';

import {
  buildReliabilityDailyTableData,
  ReliabilityDailyTableRow,
} from '../_utils/buildReliabilityDailyTableData';

import { NoData } from '@cast/shared-search-ui';

const MAX_ROWS = 30;

const renderMetricCell = (value: number | undefined, unit: string) => (
  <Typography variant="inherit">
    {dashesOrNum({
      value,
      roundBy: 2,
      mapNumber: (num) => `${num} ${unit}`,
      mapDashes: () => '-',
    })}
  </Typography>
);

const renderLatencyCell = (value: number | undefined) => (
  <Typography variant="inherit">
    {formatLatencyWithUnit(value, 2, '-')}
  </Typography>
);

const renderPercentCell = (value: number | undefined) => (
  <Typography variant="inherit">
    {value !== undefined ? `${dashesOrNum({ value, roundBy: 2 })}%` : '-'}
  </Typography>
);

type ReliabilityDailyDetailsTableProps = {
  data?: ReliabilityMetricsData;
  timezone: string;
  isLoading: boolean;
  cacheKey: string;
  urlKey?: string;
  testId: string;
};

export const ReliabilityDailyDetailsTable = ({
  data,
  timezone,
  isLoading,
  cacheKey,
  urlKey,
  testId,
}: ReliabilityDailyDetailsTableProps) => {
  const rows = useMemo(
    () => buildReliabilityDailyTableData(data, timezone, MAX_ROWS),
    [data, timezone]
  );

  if (!rows.length && !isLoading) {
    return <NoData />;
  }

  return (
    <DataTable<ReliabilityDailyTableRow>
      cacheKey={cacheKey}
      urlKey={urlKey}
      testId={testId}
      outerHeader={<TableHeader recordsCount="Daily reliability details" />}
      isLoading={isLoading}
      data={rows}
      maxRows={MAX_ROWS}
    >
      <Column<ReliabilityDailyTableRow>
        id="date"
        header="Date"
        minWidth={170}
        sortable
        renderCell={({ date }) => dayjs(date).format(DATE_SIMPLE)}
      />
      <Column<ReliabilityDailyTableRow>
        id="availabilityPercent"
        header="Availability"
        minWidth={120}
        sortable
        accessor="availabilityPercent"
        sortingType="number"
        renderCell={({ availabilityPercent }) =>
          renderPercentCell(availabilityPercent)
        }
      />
      <ColumnGroup id="request-metrics" title="Request metrics">
        <Column<ReliabilityDailyTableRow>
          id="requestRate"
          header="Req rate"
          minWidth={120}
          sortable
          accessor="requestRate"
          sortingType="number"
          renderCell={({ requestRate }) =>
            renderMetricCell(requestRate, 'req/s')
          }
        />
      </ColumnGroup>
      <Column<ReliabilityDailyTableRow>
        id="errorRatePercent"
        header="Error rate"
        minWidth={120}
        sortable
        accessor="errorRatePercent"
        sortingType="number"
        renderCell={({ errorRatePercent }) =>
          renderPercentCell(errorRatePercent)
        }
      />
      <ColumnGroup id="latency" title="Latency">
        <Column<ReliabilityDailyTableRow>
          id="latencyP50"
          header="p50"
          minWidth={100}
          sortable
          accessor="latencyP50"
          sortingType="number"
          renderCell={({ latencyP50 }) => renderLatencyCell(latencyP50)}
        />
        <Column<ReliabilityDailyTableRow>
          id="latencyP95"
          header="p95"
          minWidth={100}
          sortable
          accessor="latencyP95"
          sortingType="number"
          renderCell={({ latencyP95 }) => renderLatencyCell(latencyP95)}
        />
        <Column<ReliabilityDailyTableRow>
          id="latencyP99"
          header="p99"
          minWidth={100}
          sortable
          accessor="latencyP99"
          sortingType="number"
          renderCell={({ latencyP99 }) => renderLatencyCell(latencyP99)}
        />
      </ColumnGroup>
    </DataTable>
  );
};
